import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react";
import { useState } from "react";
import { RangePicker } from "@/components/charts";
import { DomainsPanel } from "@/features/admin/domains";
import { Empty, LastSeen, PageHeader, Rows, StatusText, UserLink } from "@/features/admin/parts";
import type { Schemas } from "@/lib/api/client";
import { type MetricsRange, usageQuery } from "@/lib/api/queries";
import { cn } from "@/lib/cn";
import { formatBytes, formatDuration, plural } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/stats")({
  component: StatsPage,
});

const RANGES = ["1h", "24h", "7d", "30d"] as const satisfies readonly MetricsRange[];

type UserUsage = Schemas["UserUsageOut"];
type ConfigUsage = Schemas["ConfigUsageOut"];

function StatsPage() {
  const [range, setRange] = useState<MetricsRange>("24h");
  const { data } = useQuery({ ...usageQuery(range), placeholderData: keepPreviousData });
  const users = data?.users ?? [];
  const max = Math.max(1, ...users.map((u) => u.rx_bytes + u.tx_bytes));
  const totals = users.reduce(
    (sum, u) => ({
      down: sum.down + u.tx_bytes,
      up: sum.up + u.rx_bytes,
      online: sum.online + u.online,
    }),
    { down: 0, up: 0, online: 0 },
  );

  return (
    <>
      <PageHeader
        title="Статистика"
        actions={<RangePicker label="Период" value={range} onChange={setRange} options={RANGES} />}
      />
      <p className="mt-2 max-w-prose text-sm text-muted">
        Трафик пользователей и их устройств за период, обновляется раз в минуту. Считается по часам:
        «1 ч» — это текущий и предыдущий час целиком. «Скачал» — то, что устройство получило из
        интернета через VPN, «отдал» — то, что оно отправило.
      </p>

      <dl className="mt-5 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-line bg-line">
        <Total label="Онлайн сейчас" value={String(totals.online)} accent />
        <Total label="Скачали" value={formatBytes(totals.down)} />
        <Total label="Отдали" value={formatBytes(totals.up)} />
      </dl>

      <div className="mt-6">
        {data && users.length === 0 ? (
          <Empty>Пользователей пока нет.</Empty>
        ) : (
          <Rows>
            {users.map((user) => (
              <UserBlock key={user.user_id ?? "none"} user={user} max={max} />
            ))}
          </Rows>
        )}
      </div>

      <DomainsPanel title="Куда ходят все" />
    </>
  );
}

function Total({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-surface px-4 py-4">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className={cn("mt-1 font-display text-lg", accent && "text-online")}>{value}</dd>
    </div>
  );
}

function Moved({ down, up }: { down: number; up: number }) {
  return (
    <span className="inline-flex items-center gap-3 text-sm tabular-nums">
      <span className="inline-flex items-center gap-1">
        <ArrowDown className="size-3" aria-label="Скачал" />
        {formatBytes(down)}
      </span>
      <span className="inline-flex items-center gap-1 text-muted">
        <ArrowUp className="size-3" aria-label="Отдал" />
        {formatBytes(up)}
      </span>
    </span>
  );
}

function UserBlock({ user, max }: { user: UserUsage; max: number }) {
  const total = user.rx_bytes + user.tx_bytes;
  const n = user.configs.length;
  return (
    <li>
      <details className="group">
        <summary className="grid cursor-pointer list-none gap-1 px-4 py-3 hover:bg-surface-2 sm:grid-cols-[1fr_10rem_12rem_1.5rem] sm:items-center sm:gap-4 [&::-webkit-details-marker]:hidden">
          <div className="min-w-0">
            <p className="truncate font-medium">
              {user.user_id === null ? "Без владельца" : user.username}
            </p>
            <p className="text-sm text-muted">
              {n} {plural(n, ["устройство", "устройства", "устройств"])}
              {user.online > 0 && <span className="text-online">, онлайн {user.online}</span>}
            </p>
          </div>
          <div aria-hidden className="h-1.5 rounded-full bg-surface-2">
            <div
              className="h-full rounded-full bg-series-1"
              style={{ width: `${(total / max) * 100}%` }}
            />
          </div>
          <Moved down={user.tx_bytes} up={user.rx_bytes} />
          <ChevronDown
            aria-hidden
            className="hidden size-4 text-muted transition-transform group-open:rotate-180 sm:block"
          />
        </summary>
        <div className="border-t border-line bg-surface-2/40 px-4 py-2">
          {n === 0 ? (
            <p className="py-2 text-sm text-muted">Конфигов нет.</p>
          ) : (
            <ul className="divide-y divide-line">
              {user.configs.map((config) => (
                <ConfigLine key={config.config_id} config={config} />
              ))}
              {user.user_id !== null && (
                <li className="py-2 text-sm">
                  <UserLink id={user.user_id} username={user.username} />
                </li>
              )}
            </ul>
          )}
        </div>
      </details>
    </li>
  );
}

function ConfigLine({ config }: { config: ConfigUsage }) {
  return (
    <li className="grid gap-1 py-2.5 sm:grid-cols-[1fr_12rem_12rem] sm:items-center sm:gap-4">
      <div className="min-w-0">
        <p className="truncate">
          {config.name}
          {config.deleted && <span className="text-sm text-muted"> · удалён владельцем</span>}
        </p>
        <p className="truncate text-sm text-muted">
          {config.address}
          {config.last_ip && <>, с {config.last_ip}</>}
        </p>
      </div>
      <div className="text-sm">
        {config.connected_since ? (
          <span className="text-online">
            онлайн {formatDuration((Date.now() - Date.parse(config.connected_since)) / 1000)}
          </span>
        ) : (
          <LastSeen iso={config.last_handshake_at} />
        )}
        {config.status !== "active" && (
          <>
            {", "}
            <StatusText status={config.status} />
          </>
        )}
      </div>
      <Moved down={config.tx_bytes} up={config.rx_bytes} />
    </li>
  );
}
